import React, { useState } from 'react'
import * as Yup from 'yup'
import { Form, Formik, Field } from 'formik'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'

import TextBox from 'components/TextBox'
import Modal from 'components/Modal'
import Accordion from 'components/Accordion'
import AccordionItem from 'components/Accordion/AccordionItem'

import { ORG_NAME } from 'utils/env'
import {
  getMerchant,
  getIsMerchantsLoading,
  getAssignedRevenueCentersToMerchant,
  getAssignedMenusToMerchant,
} from 'store/Merchants/selectors'
import { getCurrentVenueUuid } from 'store/Venues/selector'
import {
  createMerchant,
  updateMerchant,
} from 'store/Merchants/actions'

const MerchantsForm = () => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(true)

  const history = useHistory()
  const { state: urlQueryParamsState } = useLocation()
  const dispatch = useDispatch()

  const merchantUuid = urlQueryParamsState?.merchantUuid

  const onCloseModal = () =>
    history.push({ state: { merchantUuid: null } })

  const isLoading = useSelector(getIsMerchantsLoading)
  const venueUuid = useSelector(getCurrentVenueUuid)
  const merchant = useSelector((state) =>
    getMerchant(state, merchantUuid)
  )
  const assignedRevenueCentersToMerchant = useSelector((state) =>
    getAssignedRevenueCentersToMerchant(state, merchantUuid)
  )
  const assignedMenusToMerchant = useSelector((state) =>
    getAssignedMenusToMerchant(state, merchantUuid)
  )

  const { name, merchantAccountId, uuid } = merchant

  const isNewMerchant = merchantUuid === 'new'

  const modalTitle = isNewMerchant ? 'Add Merchant' : 'Edit Merchant'

  const initialValues = {
    name: name ?? '',
    merchantAccountId: merchantAccountId ?? '',
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    merchantAccountId: Yup.string().required('Required'),
  })

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))

    return disableButton
  }

  const onSubmit = (values) => {
    const additionalData = {
      venueUuid,
      organizationName: ORG_NAME,
      authorize: true,
    }

    const newMerchantData = {
      merchants: [
        { ...values, ...additionalData, merchantType: 'shift4' },
      ],
    }

    const editMerchantData = {
      merchants: [{ ...values, ...additionalData, uuid }],
    }

    if (isNewMerchant) {
      dispatch(createMerchant(newMerchantData)).then((response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { merchantUuid: null } })
      })
    } else {
      dispatch(updateMerchant(editMerchantData)).then((response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { merchantUuid: null } })
      })
    }
  }

  return (
    <Modal
      className="-narrow"
      title={modalTitle}
      onRequestClose={onCloseModal}
      onClose={onCloseModal}
      isLoading={isLoading}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, setFieldValue }) => (
            <Form className="merchant-settings">
              <div className="item-settings">
                <section className="item-settings__section">
                  <h3 className="header-title">Info</h3>
                  <div className="text-field">
                    <Field
                      component={TextBox}
                      label="Name"
                      name="name"
                    />
                  </div>
                  <div className="text-field">
                    <Field
                      component={TextBox}
                      label="Token"
                      name="merchantAccountId"
                      masked={true}
                    />
                  </div>
                </section>
                {!isNewMerchant && (
                  <section className="item-settings__section">
                    <div className="content-wrapper -flags-wrapper">
                      <Accordion header="Assigned Revenue Centers">
                        {assignedRevenueCentersToMerchant.map(
                          ({ name, uuid }) => (
                            <AccordionItem label={name} key={uuid} />
                          )
                        )}
                      </Accordion>
                    </div>
                    <div className="content-wrapper -flags-wrapper">
                      <Accordion header="Assigned Menus">
                        {assignedMenusToMerchant.map(
                          ({ name, uuid }) => (
                            <AccordionItem label={name} key={uuid} />
                          )
                        )}
                      </Accordion>
                    </div>
                  </section>
                )}
                <div className="item-settings__border-break"></div>
                <div className="btn-wrapper">
                  <button
                    className="btn -update"
                    onClick={() => setShowValidationErrors(true)}
                    disabled={disableUpdateButton(dirty, errors)}
                    type="submit"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default MerchantsForm
